/**
 * Review r29 visual (teclado): foco depois de uma ação que tira o próprio botão da tela (remover a linha, "Carregar mais"
 * na última página, alternar um botão por outro) ou que o desabilita ("Limpar filtros"). Sem isso o foco caía no body e
 * quem usa teclado voltava ao começo da página.
 */
export interface Focavel {
  focus: () => void;
  isConnected?: boolean;
  disabled?: boolean;
}

/** O `<main>` do Shell (web e portal), destino do "Pular para o conteúdo" e último recurso do foco. */
export const SELETOR_CONTEUDO = '#conteudo';

/**
 * Lote 19 (item 13, tela dividida): cada painel de conteúdo leva `data-painel-conteudo` (o `<main id="conteudo">` é o
 * "principal"; o painel ao lado, "lado") e o que tem o foco da pessoa, `data-painel-ativo`. Sem tela dividida, o principal é
 * o único e o ativo. O foco perdido volta dentro do painel de onde saiu, e a "rota" de cada painel é a dele (o painel ao lado
 * navega no próprio roteador, sem mudar o `location.pathname`).
 */
export const SELETOR_PAINEL = '[data-painel-conteudo]';
export const SELETOR_PAINEL_ATIVO = '[data-painel-conteudo][data-painel-ativo]';
let caminhoDoLado = '';
/** O painel ao lado avisa o caminho dele a cada navegação (`tela-dividida.tsx`). */
export function definirCaminhoDoLado(caminho: string) {
  caminhoDoLado = caminho;
}
/** O painel de conteúdo que contém o elemento (null fora deles: camada no body, menu, ou sem marca). */
export function painelDe(el: Element | null | undefined): HTMLElement | null {
  return (el && typeof el.closest === 'function' ? el.closest<HTMLElement>(SELETOR_PAINEL) : null) ?? null;
}
/** A rota de um painel: a do painel ao lado ou, para o principal (e fora dos painéis), a da janela. */
export function rotaDoPainel(painel: Element | null | undefined): string {
  if (painel?.getAttribute('data-painel-conteudo') === 'lado') return `lado:${caminhoDoLado}`;
  return typeof location === 'undefined' ? '' : location.pathname;
}
/** O painel da ação: o do elemento com o foco ou, com o foco fora (no body, numa camada), o painel ativo. */
export function painelDaAcao(el: Element | null | undefined = typeof document === 'undefined' ? null : document.activeElement): HTMLElement | null {
  return painelDe(el) ?? (typeof document === 'undefined' ? null : document.querySelector<HTMLElement>(SELETOR_PAINEL_ATIVO));
}
/** Último recurso do foco: o painel (se segue na tela), o painel ativo, ou o `#conteudo`. */
export function conteudoDe(painel: Element | null | undefined): HTMLElement | null {
  if (painel?.isConnected) return painel as HTMLElement;
  return document.querySelector<HTMLElement>(SELETOR_PAINEL_ATIVO) ?? document.querySelector<HTMLElement>(SELETOR_CONTEUDO);
}
/** Os elementos do seletor no painel (mais os de fora de qualquer painel, como a camada); sem painel, todos da página. */
export function noPainel<T extends Element = HTMLElement>(seletor: string, painel: Element | null | undefined): T[] {
  const todos = [...document.querySelectorAll<T>(seletor)];
  if (!painel) return todos;
  return todos.filter((x) => {
    const p = painelDe(x);
    return !p || p === painel;
  });
}

const vivo = (x: Focavel | null | undefined): x is Focavel => !!x && x.isConnected !== false && x.disabled !== true;

/**
 * Review r31 visual: ajustes finos de foco ainda em curso (`recuperarFoco`, `carregarMais`). O vigia global
 * (`vigia-foco.ts`) espera por eles antes de agir, para não escolher um alvo pior primeiro.
 */
export const LIMITE_AJUSTE_FOCO_MS = 10_000;
const rotaDoFoco = (painel?: Element | null) => rotaDoPainel(painel);
const ajustes = new Set<{ rota: string; painel: Element | null; ate: number }>();
export function ajusteDeFocoEmCurso() {
  for (const ajuste of ajustes) {
    if (ajuste.rota !== rotaDoFoco(ajuste.painel) || performance.now() >= ajuste.ate) ajustes.delete(ajuste);
  }
  return ajustes.size > 0;
}
/** Marca um ajuste fino de outro módulo (o "Tentar de novo" de estados.tsx); a função devolvida encerra a marca uma vez só. */
export function iniciarAjusteDeFoco(painel: Element | null = painelDaAcao()): () => void {
  ajusteDeFocoEmCurso();
  const ajuste = { rota: rotaDoFoco(painel), painel, ate: performance.now() + LIMITE_AJUSTE_FOCO_MS };
  ajustes.add(ajuste);
  return () => void ajustes.delete(ajuste);
}

/** O foco se perdeu: está no body, em lugar nenhum, ou num elemento que saiu da página ou ficou desabilitado. */
export function focoPerdido(ativo: unknown, corpo: unknown): boolean {
  if (ativo == null || ativo === corpo) return true;
  const a = ativo as Focavel;
  return a.isConnected === false || a.disabled === true;
}

/**
 * Para onde vai o foco: o item que ficou na posição `i` da lista (ou o último, quando o removido era o fim) e, sem lista,
 * o primeiro das reservas que segue na tela e habilitado.
 */
export function alvoDoFoco(o: { lista?: readonly Focavel[]; i?: number; reservas?: readonly (Focavel | null | undefined)[] }): Focavel | null {
  const lista = (o.lista ?? []).filter(vivo);
  if (lista.length && (o.i ?? -1) >= 0) return lista[Math.min(o.i!, lista.length - 1)]!;
  return (o.reservas ?? []).find(vivo) ?? null;
}

/** Posição do elemento entre os que casam com o seletor na página (-1 se não está entre eles). */
export function posicaoNaPagina(seletor: string, el: Element | null): number {
  // Lote 19 (tela dividida): a posição conta só no painel do elemento (a mesma tela aberta nos dois lados).
  return el ? noPainel<Element>(seletor, painelDe(el)).indexOf(el) : -1;
}

/**
 * Depois que a tela se acomoda (o React desenhou a lista nova e o modal devolveu o foco), se o foco se perdeu, leva-o ao
 * item da mesma posição entre `seletor`, senão às reservas (seletores ou funções que devolvem o elemento, na ordem) e por
 * fim ao conteúdo. Foco em outro lugar (a pessoa seguiu adiante) fica onde está.
 */
export function recuperarFoco(o: { seletor?: string; i?: number; reservas?: readonly (string | (() => HTMLElement | null))[] }) {
  // Lote 19 (tela dividida): a lista, as reservas e o conteúdo são os do painel da ação, nunca os do outro lado.
  const painel = painelDaAcao();
  const concluir = iniciarAjusteDeFoco(painel);
  const rota = rotaDoFoco(painel);
  requestAnimationFrame(() =>
    setTimeout(() => {
      concluir();
      if (rotaDoFoco(painel) !== rota) return;
      if (!focoPerdido(document.activeElement, document.body)) return;
      alvoDoFoco({
        lista: o.seletor ? noPainel<HTMLElement>(o.seletor, painel) : [],
        i: o.i,
        reservas: [...(o.reservas ?? []).map((r) => (typeof r === 'string' ? (noPainel<HTMLElement>(r, painel)[0] ?? null) : r())), conteudoDe(painel)],
      })?.focus();
    }, 0),
  );
}

const LISTAS = 'ul, ol, tbody';
const FOCAVEL = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

/**
 * A lista visível logo antes do botão (o "Carregar mais" fica depois dela): a mais externa e, com várias (blocos de uma
 * tabela), a última, que recebe os itens novos.
 */
export function listaAntesDe(botao: Element): Element | null {
  for (let el: Element | null = botao; el; el = el.parentElement) {
    for (let irmao = el.previousElementSibling; irmao; irmao = irmao.previousElementSibling) {
      const todas = [...(irmao.matches(LISTAS) ? [irmao] : []), ...irmao.querySelectorAll(LISTAS)].filter((x) => (x as HTMLElement).offsetParent !== null);
      const externas = todas.filter((x) => !todas.some((y) => y !== x && y.contains(x)));
      if (externas.length) return externas[externas.length - 1]!;
    }
  }
  return null;
}

/** A primeira lista visível logo depois do botão ("Carregar anteriores" fica acima das mensagens). */
export function listaDepoisDe(botao: Element): Element | null {
  for (let el: Element | null = botao; el; el = el.parentElement) {
    for (let irmao = el.nextElementSibling; irmao; irmao = irmao.nextElementSibling) {
      const achada = [...(irmao.matches(LISTAS) ? [irmao] : []), ...irmao.querySelectorAll(LISTAS)].find((x) => (x as HTMLElement).offsetParent !== null);
      if (achada) return achada;
    }
  }
  return null;
}

/** O que recebe o foco num item: ele mesmo se é focável, senão o primeiro controle dentro dele, senão ele com tabIndex -1. */
export function focavelDoItem(item: HTMLElement): HTMLElement {
  if (item.matches(FOCAVEL)) return item;
  const dentro = item.querySelector<HTMLElement>(FOCAVEL);
  if (dentro) return dentro;
  item.tabIndex = -1;
  return item;
}

/** Itens visíveis de uma Tabela (linhas do desktop e cartões do celular), na ordem da tela. */
export function itensVisiveisDaTabela(raiz: Element | null): Element[] {
  return [...(raiz?.querySelectorAll('tbody > tr:not([aria-hidden]), [role="listitem"]') ?? [])].filter((x) => (x as HTMLElement).offsetParent !== null);
}

/**
 * Review r29 visual (B1): "Carregar mais" some na última página com o foco nele, que caía no body. Depois de carregar, se
 * o foco se perdeu, ele vai ao primeiro item novo da lista (ou ao último, se nada veio) e, sem lista, ao conteúdo.
 */
export function carregarMais(buscar: () => Promise<unknown>, botao: Element, opcoes: { itens?: () => Element[]; anteriores?: boolean } = {}) {
  // Review r30 visual (M1): quem tem a lista fora do padrão (a Tabela: cartões em `div role=list` no celular e um `tbody` por
  // página no desktop) passa os itens; sem isso a busca saía da Tabela e achava a barra de abas do módulo.
  // Review r30 visual (B1): "Carregar anteriores" (Conversas, Equipe) fica ANTES da lista e os itens novos entram no topo:
  // o foco vai ao mais recente dos que chegaram, logo acima de onde a pessoa estava.
  const itensDaLista = opcoes.itens;
  const lista = itensDaLista ? null : opcoes.anteriores ? listaDepoisDe(botao) : listaAntesDe(botao);
  const itensAgora = () => (itensDaLista ? itensDaLista() : lista?.isConnected ? [...lista.children] : []);
  const antes = itensAgora().length;
  // Antes da marca: exceção aqui não deixa o ajuste preso até o prazo (r35 robustez B2).
  const regiao = painelDe(botao) ?? botao.closest(SELETOR_CONTEUDO);
  const concluir = iniciarAjusteDeFoco(regiao);
  const rota = rotaDoFoco(regiao);
  // Review r31 robustez (B1) e visual (B6): a Tabela virtualiza e o bloco da página nova monta só quando o
  // IntersectionObserver responde (cerca de 200 ms depois). Espera os itens novos aparecerem, até `ESPERA_ITENS_MS`; antes,
  // o foco ia para a última linha antiga.
  const decidir = (inicio: number) => {
    if (rotaDoFoco(regiao) !== rota || (regiao && !regiao.isConnected)) return concluir();
    const itens = itensAgora();
    if (itens.length <= antes && performance.now() - inicio < ESPERA_ITENS_MS) return void setTimeout(() => decidir(inicio), 50);
    concluir();
    if (!focoPerdido(document.activeElement, document.body)) return;
    const i = opcoes.anteriores ? Math.max(itens.length - antes - 1, 0) : Math.min(antes, itens.length - 1);
    const item = itens[i] as HTMLElement | undefined;
    (item ? focavelDoItem(item) : conteudoDe(regiao))?.focus();
  };
  try {
    void buscar().then(
      () => requestAnimationFrame(() => setTimeout(() => decidir(performance.now()), 0)),
      concluir,
    );
  } catch (erro) {
    concluir();
    throw erro;
  }
}

/** Teto da espera pelos itens novos de "Carregar mais" (a Tabela monta o bloco novo pelo IntersectionObserver). */
export const ESPERA_ITENS_MS = 800;

/**
 * Review r34 visual B1: põe a aba ativa na vista rolando só o `scrollLeft` da faixa que rola na horizontal. O
 * `scrollIntoView` movia o ponto de partida do Tab do Chromium para a aba: abrindo a tela pela URL, o primeiro Tab pulava o
 * "Pular para o conteúdo", o menu e o topo. `centro` centraliza a aba; `perto` só rola se ela estiver fora da vista.
 */
export function rolarAbaAtiva(ativa: HTMLElement, modo: 'centro' | 'perto', suave = false) {
  let faixa = ativa.parentElement;
  while (faixa && !(faixa.scrollWidth > faixa.clientWidth && /auto|scroll/.test(getComputedStyle(faixa).overflowX))) faixa = faixa.parentElement;
  if (!faixa) return;
  const a = ativa.getBoundingClientRect();
  const f = faixa.getBoundingClientRect();
  const esquerda = a.left - f.left + faixa.scrollLeft;
  let alvo: number;
  if (modo === 'centro') alvo = esquerda - (faixa.clientWidth - a.width) / 2;
  else if (a.left >= f.left && a.right <= f.right) return;
  else alvo = a.left < f.left ? esquerda : esquerda - faixa.clientWidth + a.width;
  const left = Math.max(0, alvo);
  if (typeof faixa.scrollTo === 'function') faixa.scrollTo({ left, behavior: suave ? 'smooth' : 'auto' });
  else faixa.scrollLeft = left;
}

/** O foco do teclado está em outro painel da tela dividida, e não no do elemento? */
export function focoEmOutroPainel(el: Element): boolean {
  const painel = document.activeElement?.closest('[data-painel]');
  return !!painel && painel !== el.closest('[data-painel]');
}
